import { Menu, Search } from 'lucide-react';
import { useAuth } from '@/hooks';
import { ThemeToggle } from './theme-toggle';
import { NotificationBell } from '@/components/NotificationBell';

interface TopbarProps {
  onMenuClick: () => void;
  children?: React.ReactNode;
}

export function Topbar({ onMenuClick, children }: TopbarProps) {
  const { user } = useAuth();

  const roleLabel =
    user?.role === 'DOCTOR'
      ? 'Médico'
      : user?.role === 'PATIENT'
      ? 'Paciente'
      : user?.role === 'ADMIN'
      ? 'Administrador'
      : user?.role || '';

  return (
    <header className="h-18 bg-card/90 dark:bg-slate-950/90 backdrop-blur-xl border-b border-border/80 dark:border-white/[0.08] flex items-center justify-between px-5 sm:px-6 transition-all duration-200 sticky top-0 z-40">
      {/* Left Area: Mobile Menu & Enhanced Search Pill */}
      <div className="flex items-center gap-4 sm:gap-6">
        <button
          onClick={onMenuClick}
          className="p-2.5 rounded-xl hover:bg-muted/80 text-foreground transition-all lg:hidden cursor-pointer active:scale-95 border border-border/60"
          aria-label="Abrir menu"
        >
          <Menu size={20} />
        </button>

        <div className="relative hidden sm:block w-72 md:w-84">
          <Search
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <input
            type="text"
            placeholder="Buscar no sistema..."
            className="w-full pl-10 pr-12 py-2 rounded-full border border-border/80 bg-background/80 text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-2xs hover:border-border"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted-foreground bg-muted/60 border border-border/80 px-1.5 py-0.5 rounded-md pointer-events-none">
            Ctrl+K
          </kbd>
        </div>
      </div>

      {/* Right Area: Children, ThemeToggle, Notifications & User Capsule */}
      <div className="flex items-center gap-3 sm:gap-4 lg:gap-5">
        {children}

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <NotificationBell />
        </div>

        <div className="h-5 w-px bg-border/80 dark:bg-white/10 hidden sm:block" />

        <div className="flex items-center gap-3 p-1 sm:pr-3 rounded-full hover:bg-muted/40 transition-colors">
          <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-sm ring-2 ring-primary/20">
            {(user?.name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
          </div>
          <div className="hidden md:block text-left">
            <p className="text-xs sm:text-sm font-bold text-foreground truncate max-w-[170px] leading-tight">
              {user?.name || user?.email}
            </p>
            <span className="inline-block text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.2 rounded-full mt-0.5">
              {roleLabel}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
}
